// Ward Rounds & Medications — 2nd Inpatient Care simulator.
// Reading the hospital treatment sheet, the medication "rights",
// dose-volume calculation, safe administration and sign-off.
// Purely client-side, single-session. Pitched at Cert IV Veterinary Nursing.
//
// Five staged beats per case:
//   1. Read the treatment sheet   (choice · 20)
//   2. Medication rights check     (chips · 20)
//   3. Calculate the dose volume   (dose mg 10 + volume mL 20 = 30)
//   4. Route & technique           (choice · 15)
//   5. Record & sign-off + handover(choice · 15)
// Total budget = 100 points → score is already a percentage.

// Map an option score (3 best / 1 partial / 0 wrong) onto a points budget.
export const optPts = (score, full, partial) =>
  score >= 3 ? full : score === 1 ? partial : 0;

// Grade a numeric answer against the correct value (percentage tolerance).
export const gradeNumber = (answer, correct, full) => {
  const a = parseFloat(answer);
  if (isNaN(a) || !(correct > 0)) return { band: "wrong", pts: 0 };
  const err = Math.abs(a - correct) / correct;
  if (err <= 0.02) return { band: "correct", pts: full };
  if (err <= 0.2) return { band: "partial", pts: Math.round(full / 2) };
  return { band: "wrong", pts: 0 };
};

// Score the tappable medication-rights checklist (out of 20).
export const gradeRights = (chips, selectedSet) => {
  const trueIdx = chips.map((x, i) => (x.flag ? i : -1)).filter((i) => i >= 0);
  const caught = trueIdx.filter((i) => selectedSet.has(i)).length;
  const falseTaps = [...selectedSet].filter((i) => !chips[i].flag).length;
  const pts = Math.max(0, Math.min(20, Math.round((20 * caught) / trueIdx.length) - 5 * falseTaps));
  return { pts, caught, total: trueIdx.length, falseTaps };
};

// Correct draw-up volume in mL for a weight-based dose.
export const doseMg = (c) => c.calc.weightKg * c.calc.doseMgKg;
export const doseVolume = (c) => doseMg(c) / c.calc.concMgPerMl;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Return a copy of a case with selectable options / chips shuffled (replay value).
export const serveCase = (c) => ({
  ...c,
  sheetTask: { ...c.sheetTask, options: shuffle(c.sheetTask.options) },
  rights: { ...c.rights, chips: shuffle(c.rights.chips) },
  routeTask: { ...c.routeTask, options: shuffle(c.routeTask.options) },
  record: { ...c.record, options: shuffle(c.record.options) },
});

export const WARD_ROUNDS_CASES = [
  {
    id: "bruno-postop",
    name: "Bruno",
    species: "dog",
    breed: "German Shepherd",
    signalment: "5 yr · male neutered",
    weightLabel: "32 kg",
    ward: "Surgical ward · Kennel 4",
    when: "Day 1 post-op · 10:00 medication round",
    nowTime: "10:00",
    briefing:
      "Bruno had an exploratory laparotomy yesterday. You're doing the 10:00 round. His treatment sheet lists IV fluids, an opioid for pain and an antibiotic. The methadone written up for 10:00 has not yet been initialled.",
    // ── Beat 1 · Read the treatment sheet ──────────────────────────────────
    sheet: {
      title: "Inpatient treatment sheet · Bruno (32 kg)",
      rows: [
        { time: "06:00", drug: "Methadone 0.3 mg/kg IV", freq: "q6h", status: "signed", note: "Given 06:05 — JT" },
        { time: "08:00", drug: "Amoxicillin-clav 12.5 mg/kg IV", freq: "q12h", status: "signed", note: "Given 08:10 — JT" },
        { time: "10:00", drug: "Methadone 0.3 mg/kg IV", freq: "q6h", status: "due", note: "Not yet given" },
        { time: "10:00", drug: "Hartmann's CRI", freq: "60 mL/h", status: "running", note: "Bag 2 — 320 mL left" },
        { time: "14:00", drug: "Amoxicillin-clav 12.5 mg/kg IV", freq: "q12h", status: "later", note: "Not due yet" },
      ],
    },
    sheetTask: {
      prompt: "It's 10:00. Reading the treatment sheet, what is due to be given right now?",
      options: [
        { t: "The 10:00 methadone dose — it's written up, due now and not yet initialled. The fluids are already running and the next antibiotic isn't due until 14:00.", score: 3, note: "Correct — only the 10:00 opioid is outstanding; check the CRI is running at the ordered rate as you pass." },
        { t: "The antibiotic — give the 14:00 amoxicillin-clav early to save a trip.", score: 0, note: "Never bring forward a timed antibiotic; doses are spaced for therapeutic levels." },
        { t: "Nothing — the 08:00 row is already initialled so the round is done.", score: 0, note: "The 10:00 methadone is due and unsigned — it would be missed." },
        { t: "Re-give the 06:00 methadone because the patient still seems sore.", score: 1, note: "Pain may need addressing, but you give the dose that is due (10:00), and escalate breakthrough pain to the vet rather than repeating an early dose." },
      ],
    },
    // ── Beat 2 · Medication rights ──────────────────────────────────────────
    rights: {
      prompt: "Before you draw up and give the 10:00 methadone, run the medication checks. Tap every genuine concern you must resolve first.",
      chips: [
        { t: "Patient identity not confirmed against the kennel card / microchip", flag: true, coach: "Right patient: positively identify Bruno before giving any drug." },
        { t: "The 10:00 dose has not been initialled — was it already given?", flag: true, coach: "An unsigned due dose must be clarified before giving, to avoid a double dose." },
        { t: "Methadone is a controlled (S8) drug — register entry & witness needed", flag: true, coach: "Right documentation: controlled drugs require a dangerous-drugs register entry and (per practice policy) a witness." },
        { t: "Drug, dose, route and time match the chart and the vet's order", flag: false, coach: "" },
        { t: "Drawing-up syringe is clean and the vial is in date", flag: false, coach: "" },
        { t: "No reason to check the chart — the morning nurse already did", flag: true, coach: "Every administration is independently checked against the chart — you can't rely on a previous shift." },
        { t: "Patient is bright and the IV line is patent and clean", flag: false, coach: "" },
      ],
    },
    // ── Beat 3 · Dose calculation ──────────────────────────────────────────
    calc: {
      drug: "Methadone",
      weightKg: 32,
      doseMgKg: 0.3,
      concMgPerMl: 10,
      route: "IV",
      prompt:
        "Methadone is ordered at 0.3 mg/kg IV for Bruno (32 kg). The vial concentration is 10 mg/mL. Work out the total dose and the volume to draw up.",
    },
    // ── Beat 4 · Route & technique ──────────────────────────────────────────
    routeTask: {
      prompt: "The chart specifies the IV route via Bruno's cephalic catheter. What's the safe technique?",
      options: [
        { t: "Check catheter patency with a saline flush, give the methadone slowly IV as a bolus, then flush again — watching Bruno for sedation/respiratory effects.", score: 3, note: "Confirm patency, give slowly, flush the line and monitor the patient afterwards." },
        { t: "Inject it quickly so you can move on to the next kennel.", score: 0, note: "Rapid IV opioids can cause excitation, bradycardia and respiratory depression — give slowly." },
        { t: "Give it IM instead — it's easier than using the catheter.", score: 0, note: "Changing the prescribed route without veterinary direction is a medication error." },
        { t: "Give it IV but skip the flush to save the saline.", score: 1, note: "Always flush a catheter before and after — to confirm patency and clear the line." },
      ],
    },
    // ── Beat 5 · Record & handover ──────────────────────────────────────────
    record: {
      prompt: "You've given the methadone. How do you complete the round?",
      options: [
        { t: "Initial the 10:00 dose with the exact time given, complete the controlled-drug register with a witness, note Bruno's pain score and check the CRI rate, then flag any concerns to the vet.", score: 3, note: "Contemporaneous sign-off, S8 register, a pain score and a fluid check — a complete, auditable round." },
        { t: "Sign the dose at the end of your shift when you have a moment.", score: 0, note: "Records must be contemporaneous — sign immediately after giving, or doses get missed or duplicated." },
        { t: "Tell the next nurse verbally that you gave it; no need to write it down.", score: 0, note: "Verbal handover doesn't replace the written, signed record." },
        { t: "Initial the dose but leave the controlled-drug register for later.", score: 1, note: "The S8 register must be completed at the time of administration, not deferred." },
      ],
    },
  },

  {
    id: "misty-ckd",
    name: "Misty",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "13 yr · female spayed",
    weightLabel: "3.8 kg",
    ward: "Cat ward · Kennel 2",
    when: "Day 2 · 08:00 medication round",
    nowTime: "08:00",
    briefing:
      "Misty is a hospitalised CKD cat who has been nauseous and not eating. You're doing the 08:00 round. Her sheet lists maintenance fluids, an anti-emetic and a phosphate binder with food. She vomited overnight and the nurse before you queried whether the maropitant dose looked high.",
    sheet: {
      title: "Inpatient treatment sheet · Misty (3.8 kg)",
      rows: [
        { time: "08:00", drug: "Maropitant 1 mg/kg SC", freq: "q24h", status: "due", note: "Anti-emetic — not yet given" },
        { time: "08:00", drug: "Hartmann's CRI", freq: "11 mL/h", status: "running", note: "Maintenance — bag 1" },
        { time: "08:00", drug: "Phosphate binder", freq: "with food", status: "hold", note: "Not eating — see note" },
        { time: "20:00", drug: "Maropitant 1 mg/kg SC", freq: "q24h?", status: "query", note: "Duplicate written by night vet" },
      ],
    },
    sheetTask: {
      prompt: "It's 08:00. What does the treatment sheet tell you to do this round?",
      options: [
        { t: "Give the 08:00 maropitant SC, leave the CRI running, and hold the phosphate binder because it's a 'with food' drug and Misty isn't eating — then query the duplicate 20:00 maropitant with the vet.", score: 3, note: "Correct — give what's due, recognise the 'with food' binder can't be given to a fasting cat, and flag the apparent duplicate q24h order." },
        { t: "Give the maropitant now AND the 20:00 dose now so you don't have to come back.", score: 0, note: "Maropitant is once daily (q24h) — giving both is a double dose. Clarify the duplicate, don't stack it." },
        { t: "Force-feed Misty so you can give the phosphate binder on schedule.", score: 0, note: "Don't force-feed to fit a drug round; a 'with food' binder is simply held until she eats and the inappetence is flagged." },
        { t: "Skip the maropitant — she already vomited so it won't stay down.", score: 1, note: "Maropitant is given by injection (SC) precisely because the patient is vomiting; it isn't withheld for that reason." },
      ],
    },
    rights: {
      prompt: "Before giving Misty's 08:00 maropitant, run your checks. Tap every genuine concern you must resolve first.",
      chips: [
        { t: "The night vet wrote a second maropitant at 20:00 — a possible duplicate q24h order", flag: true, coach: "Two once-daily orders for the same drug is a duplication risk — clarify before giving, never give both." },
        { t: "Previous nurse queried the dose as 'high' — recheck mg/kg against the chart", flag: true, coach: "A flagged dose query must be resolved (recalculated/confirmed) before administration." },
        { t: "Confirm Misty's identity against the kennel card before giving", flag: true, coach: "Right patient — always positively identify before any drug." },
        { t: "Maropitant 1 mg/kg SC is the standard feline dose and route", flag: false, coach: "" },
        { t: "The SC injection site and skin are clean and intact", flag: false, coach: "" },
        { t: "She's a long-stay patient so the checks can be skipped today", flag: true, coach: "Familiarity never removes the need for the medication checks on every dose." },
        { t: "The maropitant vial is in date and not discoloured", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Maropitant",
      weightKg: 3.8,
      doseMgKg: 1,
      concMgPerMl: 10,
      route: "SC",
      prompt:
        "Maropitant is ordered at 1 mg/kg SC for Misty (3.8 kg). The vial concentration is 10 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies maropitant SC. What's the correct technique for this cat?",
      options: [
        { t: "Give it subcutaneously into a loose scruff/flank fold using low-stress handling, noting that SC maropitant can sting — then settle her gently afterwards.", score: 3, note: "Correct SC technique with low-stress handling; maropitant is known to sting, so handle calmly." },
        { t: "Give it IV down the fluid line — it'll be less painful.", score: 0, note: "Don't change a prescribed SC drug to IV without veterinary direction." },
        { t: "Scruff her firmly and inject quickly regardless of her stress.", score: 0, note: "Forceful handling raises stress in a sick CKD cat — use low-stress techniques." },
        { t: "Give it SC but into the same spot used yesterday without checking the skin.", score: 1, note: "Vary the site and check the skin is healthy before injecting." },
      ],
    },
    record: {
      prompt: "You've given the maropitant SC. How do you complete the round?",
      options: [
        { t: "Initial the 08:00 dose with the time given, record that the binder was held (not eating) and that she's still inappetent, query the duplicate 20:00 order with the vet, and check the CRI rate.", score: 3, note: "Signs off what was given, documents the held dose and the reason, escalates the duplicate, and verifies fluids — exactly right." },
        { t: "Sign for the binder as well so the sheet looks complete.", score: 0, note: "Never sign for a drug you didn't give — that's a falsified record and a safety risk." },
        { t: "Just give the dose and tidy up; the vet will sort the chart later.", score: 0, note: "You must sign your own administration and actively flag the duplicate order now." },
        { t: "Initial the maropitant but don't bother noting that she isn't eating.", score: 1, note: "Inappetence in a CKD cat is clinically important and must be recorded and handed over." },
      ],
    },
  },

  {
    id: "rosie-parvo",
    name: "Rosie",
    species: "dog",
    breed: "Staffordshire Bull Terrier pup",
    signalment: "5 mo · female entire",
    weightLabel: "8 kg",
    ward: "Isolation ward · Kennel 1",
    when: "Day 1 · 12:00 medication round",
    nowTime: "12:00",
    briefing:
      "Rosie is a parvovirus puppy in isolation on IV fluids, anti-emetics and antibiotics. You're doing the 12:00 round in barrier nursing. The maropitant written for 12:00 hasn't been initialled.",
    sheet: {
      title: "Inpatient treatment sheet · Rosie (8 kg) · ISOLATION",
      rows: [
        { time: "06:00", drug: "Maropitant 1 mg/kg IV", freq: "q24h", status: "signed", note: "Given 06:05 — AB" },
        { time: "12:00", drug: "Maropitant 1 mg/kg IV", freq: "q24h?", status: "query", note: "Second order written by day vet" },
        { time: "12:00", drug: "Amoxicillin-clav 20 mg/kg IV", freq: "q8h", status: "due", note: "Not yet given" },
        { time: "12:00", drug: "Hartmann's CRI", freq: "20 mL/h", status: "running", note: "With KCl supplement" },
      ],
    },
    sheetTask: {
      prompt: "It's 12:00 in isolation. What does the sheet tell you to do?",
      options: [
        { t: "Give the 12:00 antibiotic that's due, query the duplicate 12:00 maropitant (already given q24h at 06:00), and check the CRI rate — all within barrier-nursing precautions.", score: 3, note: "Correct — give what's genuinely due, flag the apparent duplicate once-daily order, and verify the fluids." },
        { t: "Give both the maropitant and the antibiotic now to catch up.", score: 0, note: "The maropitant is once daily and was given at 06:00 — repeating it is a double dose." },
        { t: "Skip everything to avoid entering isolation again.", score: 0, note: "Due medications can't be skipped; follow barrier nursing and give what's due." },
        { t: "Give the antibiotic but reduce the fluid rate to save the bag.", score: 1, note: "Don't alter a prescribed CRI rate without veterinary direction; give the antibiotic." },
      ],
    },
    rights: {
      prompt: "Before giving Rosie's 12:00 antibiotic, run your checks. Tap every genuine concern to resolve first.",
      chips: [
        { t: "A second maropitant is written for 12:00 — likely a duplicate q24h order", flag: true, coach: "Clarify the apparent duplicate before giving; never stack a once-daily drug." },
        { t: "Confirm Rosie's identity against the isolation kennel card", flag: true, coach: "Right patient — identify before any drug, even in isolation." },
        { t: "Barrier nursing / PPE and order of patients to avoid spreading parvovirus", flag: true, coach: "Infection control is part of safe administration in an isolation case." },
        { t: "Drug, dose, route and time of the antibiotic match the chart", flag: false, coach: "" },
        { t: "IV catheter is patent and the site is clean", flag: false, coach: "" },
        { t: "She's only a puppy so the checks can be relaxed", flag: true, coach: "Paediatric patients need MORE care with dosing, not less." },
        { t: "Antibiotic vial is in date and reconstituted correctly", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Amoxicillin-clav",
      weightKg: 8,
      doseMgKg: 20,
      concMgPerMl: 100,
      route: "IV",
      prompt: "Amoxicillin-clav is ordered at 20 mg/kg IV for Rosie (8 kg). The reconstituted concentration is 100 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies IV via Rosie's catheter. What's the safe technique?",
      options: [
        { t: "Check catheter patency with a flush, give the antibiotic slowly IV at the recommended rate, then flush — maintaining barrier-nursing hygiene throughout.", score: 3, note: "Confirm patency, give at the correct rate, flush, and keep infection-control measures." },
        { t: "Inject it as a rapid bolus to minimise time in isolation.", score: 0, note: "Give IV antibiotics at the recommended rate; rapid injection can cause reactions." },
        { t: "Give it IM because the catheter is fiddly in a wriggly pup.", score: 0, note: "Don't change a prescribed IV route without veterinary direction." },
        { t: "Give it IV but skip the flush to save time.", score: 1, note: "Always flush before and after to confirm patency and clear the line." },
      ],
    },
    record: {
      prompt: "You've given the antibiotic. How do you complete the round?",
      options: [
        { t: "Initial the 12:00 antibiotic with the time given, query the duplicate maropitant with the vet, note her demeanour/any vomiting and the CRI rate, and dispose of PPE/sharps correctly.", score: 3, note: "Contemporaneous sign-off, escalation of the duplicate, clinical notes and correct isolation waste handling." },
        { t: "Sign for both the antibiotic and the maropitant to tidy the sheet.", score: 0, note: "Never sign for a drug you didn't (and shouldn't) give." },
        { t: "Verbally tell the next nurse and leave the sheet blank.", score: 0, note: "Records must be written and contemporaneous." },
        { t: "Initial the antibiotic but leave the duplicate query for the next shift.", score: 1, note: "Flag the duplicate now — it could lead to a double dose at the next round." },
      ],
    },
  },

  {
    id: "felix-fracture-cat",
    name: "Felix",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "4 yr · male neutered",
    weightLabel: "4.5 kg",
    ward: "Cat ward · Kennel 3",
    when: "Day 1 post-op · 14:00 medication round",
    nowTime: "14:00",
    briefing:
      "Felix had a femoral fracture repair this morning. He's on multimodal analgesia. The 14:00 buprenorphine (oral transmucosal) hasn't been initialled and he seems uncomfortable.",
    sheet: {
      title: "Inpatient treatment sheet · Felix (4.5 kg)",
      rows: [
        { time: "08:00", drug: "Buprenorphine 0.02 mg/kg OTM", freq: "q8h", status: "signed", note: "Given 08:10 — KT" },
        { time: "14:00", drug: "Buprenorphine 0.02 mg/kg OTM", freq: "q8h", status: "due", note: "Not yet given" },
        { time: "14:00", drug: "Meloxicam 0.05 mg/kg PO", freq: "q24h", status: "signed", note: "Given with food 13:00 — KT" },
        { time: "14:00", drug: "Hartmann's CRI", freq: "12 mL/h", status: "running", note: "Maintenance" },
      ],
    },
    sheetTask: {
      prompt: "It's 14:00 and Felix looks sore. What's due now?",
      options: [
        { t: "The 14:00 buprenorphine (OTM) — it's due and unsigned; the meloxicam was already given at 13:00 and the CRI is running.", score: 3, note: "Correct — give the outstanding opioid; the NSAID is done and the fluids are running." },
        { t: "Re-give the meloxicam because he still seems sore.", score: 0, note: "The NSAID is once daily and already given — repeating it risks toxicity; give the due opioid and reassess pain." },
        { t: "Nothing — he had buprenorphine at 08:00.", score: 0, note: "The 14:00 q8h dose is due and unsigned — it would be missed." },
        { t: "Give the buprenorphine IV instead because it's faster.", score: 1, note: "Address pain, but don't change the prescribed OTM route without veterinary direction." },
      ],
    },
    rights: {
      prompt: "Before giving the 14:00 buprenorphine OTM, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "The 14:00 dose is unsigned — confirm it wasn't already given", flag: true, coach: "An unsigned due dose must be clarified to avoid a double dose." },
        { t: "Buprenorphine is a controlled (S8) drug — register entry & witness", flag: true, coach: "Controlled drugs need a dangerous-drugs register entry and (per policy) a witness." },
        { t: "Confirm Felix's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Drug, dose, route (OTM) and time match the chart", flag: false, coach: "" },
        { t: "His pain score should be assessed and recorded", flag: true, coach: "Assess and document pain so analgesia can be adjusted if inadequate." },
        { t: "He's a regular here so checks can be skipped", flag: true, coach: "Familiarity never removes the medication checks." },
        { t: "The buprenorphine ampoule is in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Buprenorphine",
      weightKg: 4.5,
      doseMgKg: 0.02,
      concMgPerMl: 0.3,
      route: "OTM",
      prompt: "Buprenorphine is ordered at 0.02 mg/kg OTM for Felix (4.5 kg). The ampoule concentration is 0.3 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies the oral transmucosal (OTM) route. What's the correct technique for this cat?",
      options: [
        { t: "Place the measured dose into the cheek pouch/onto the gums using low-stress handling, allowing absorption across the mucosa — not swallowed.", score: 3, note: "OTM buprenorphine is absorbed across the oral mucosa; gentle handling keeps the dose in place." },
        { t: "Squirt it down the throat so he definitely swallows it.", score: 0, note: "Swallowing reduces OTM absorption — deposit it onto the mucosa, not the pharynx." },
        { t: "Give it IM because OTM is messy.", score: 0, note: "Don't change the prescribed route without veterinary direction." },
        { t: "Give OTM but scruff him firmly and force his mouth open.", score: 1, note: "Use low-stress handling; forceful restraint raises stress and risks a bite." },
      ],
    },
    record: {
      prompt: "You've given the buprenorphine OTM. How do you complete the round?",
      options: [
        { t: "Initial the 14:00 dose with the time given, complete the controlled-drug register with a witness, record his pain score and reassess, and check the CRI.", score: 3, note: "Contemporaneous sign-off, S8 register, pain scoring and a fluid check — complete and auditable." },
        { t: "Sign it at the end of the shift.", score: 0, note: "Records must be contemporaneous." },
        { t: "Initial the dose but skip the controlled-drug register.", score: 0, note: "The S8 register must be completed at the time of administration." },
        { t: "Record the dose but don't bother with a pain score.", score: 1, note: "A pain score guides whether his analgesia is adequate — record it." },
      ],
    },
  },

  {
    id: "duke-osteoarthritis",
    name: "Duke",
    species: "dog",
    breed: "Labrador Retriever",
    signalment: "9 yr · male neutered",
    weightLabel: "34 kg",
    ward: "Medical ward · Kennel 6",
    when: "Day 1 · 09:00 medication round",
    nowTime: "09:00",
    briefing:
      "Duke is hospitalised for investigation of lethargy and is on a once-daily NSAID for osteoarthritis. The 09:00 meloxicam is due. The night nurse noted he vomited once overnight and hasn't eaten breakfast.",
    sheet: {
      title: "Inpatient treatment sheet · Duke (34 kg)",
      rows: [
        { time: "09:00", drug: "Meloxicam 0.1 mg/kg PO", freq: "q24h", status: "due", note: "Give with food" },
        { time: "09:00", drug: "Hartmann's CRI", freq: "70 mL/h", status: "running", note: "Bag 2" },
        { time: "12:00", drug: "Gabapentin 10 mg/kg PO", freq: "q12h", status: "later", note: "Not due yet" },
      ],
    },
    sheetTask: {
      prompt: "It's 09:00. Duke vomited overnight and hasn't eaten. What do you do about the meloxicam?",
      options: [
        { t: "Hold the meloxicam and flag the overnight vomiting and inappetence to the vet before giving an NSAID — they may withhold it.", score: 3, note: "Correct — NSAIDs are withheld in vomiting/inappetent or dehydrated patients (GI and renal risk); escalate before giving." },
        { t: "Give the meloxicam now on an empty stomach to keep on schedule.", score: 0, note: "Giving an NSAID to a vomiting, fasting dog risks GI ulceration and renal injury." },
        { t: "Double the next dose to make up for holding it.", score: 0, note: "Never double an NSAID dose — that markedly increases toxicity." },
        { t: "Force-feed him so you can give it with food.", score: 0, note: "Don't force-feed to fit a drug round; hold and flag the inappetence." },
      ],
    },
    rights: {
      prompt: "You're deciding about the 09:00 meloxicam. Tap every genuine concern you must resolve first.",
      chips: [
        { t: "He vomited overnight — a contraindication to giving an NSAID now", flag: true, coach: "GI signs are a red flag for NSAIDs; withhold and flag to the vet." },
        { t: "He hasn't eaten — meloxicam is a 'with food' drug", flag: true, coach: "An empty stomach increases GI risk; hold until eating and cleared by the vet." },
        { t: "Confirm hydration — NSAIDs are risky if dehydrated", flag: true, coach: "Check perfusion/hydration before any NSAID; renal injury risk if hypovolaemic." },
        { t: "Confirm Duke's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Drug, dose and route match the chart", flag: false, coach: "" },
        { t: "NSAIDs are completely safe so just give it", flag: true, coach: "NSAIDs carry real GI/renal risks, especially in a vomiting patient." },
        { t: "The meloxicam bottle is in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Meloxicam",
      weightKg: 34,
      doseMgKg: 0.1,
      concMgPerMl: 1.5,
      route: "PO",
      prompt: "IF the vet confirms it's safe, meloxicam is ordered at 0.1 mg/kg PO for Duke (34 kg). The oral suspension is 1.5 mg/mL. Work out the total dose and the volume.",
    },
    routeTask: {
      prompt: "The vet reviews Duke, the vomiting has settled, he's eaten a little and is well hydrated — they OK the meloxicam. What's the correct technique?",
      options: [
        { t: "Give the measured oral suspension with/after food, ensuring he swallows it, and continue to monitor for further GI signs.", score: 3, note: "Give with food to reduce GI upset and keep monitoring." },
        { t: "Give it IV for faster effect.", score: 0, note: "Don't change a prescribed oral dose to IV without direction." },
        { t: "Mix it into a huge bowl of food he may not finish.", score: 1, note: "If he doesn't finish it, the dose is uncertain — give the measured dose directly with a little food." },
        { t: "Give double now since you held the earlier dose.", score: 0, note: "Never double an NSAID dose." },
      ],
    },
    record: {
      prompt: "How do you record this round?",
      options: [
        { t: "Document that the dose was initially HELD and why (overnight vomiting/inappetence), the vet review, then the time given once cleared, plus ongoing GI monitoring.", score: 3, note: "A complete record of the hold, the reason, the escalation and the eventual administration." },
        { t: "Just initial the meloxicam at 09:00 as if nothing happened.", score: 0, note: "The hold, reason and vet review must be documented for safety and audit." },
        { t: "Write 'given' but don't mention the vomiting.", score: 0, note: "Never omit safety-relevant clinical information." },
        { t: "Note it was given but forget to record the vet review.", score: 1, note: "The vet's decision to proceed is an important part of the record." },
      ],
    },
  },

  {
    id: "shadow-seizure",
    name: "Shadow",
    species: "dog",
    breed: "Border Collie",
    signalment: "6 yr · male neutered",
    weightLabel: "20 kg",
    ward: "Medical ward · Kennel 2",
    when: "Day 2 · 08:00 medication round",
    nowTime: "08:00",
    briefing:
      "Shadow is an epileptic hospitalised after a cluster of seizures. He's on twice-daily phenobarbital. The 08:00 dose is due. Timing of anti-epileptic drugs is critical for stable blood levels.",
    sheet: {
      title: "Inpatient treatment sheet · Shadow (20 kg)",
      rows: [
        { time: "20:00", drug: "Phenobarbital 2.5 mg/kg PO", freq: "q12h", status: "signed", note: "Given 20:05 — JP" },
        { time: "08:00", drug: "Phenobarbital 2.5 mg/kg PO", freq: "q12h", status: "due", note: "Not yet given" },
        { time: "08:00", drug: "Hartmann's CRI", freq: "45 mL/h", status: "running", note: "Maintenance" },
      ],
    },
    sheetTask: {
      prompt: "It's 08:00. What's the priority with Shadow's anti-epileptic?",
      options: [
        { t: "Give the 08:00 phenobarbital on time — consistent q12h timing keeps blood levels stable and reduces breakthrough seizures.", score: 3, note: "Correct — anti-epileptic timing must be consistent; give it now and record the exact time." },
        { t: "Delay it a few hours; the timing of epilepsy drugs doesn't matter much.", score: 0, note: "Erratic timing destabilises blood levels and risks breakthrough seizures." },
        { t: "Skip it because he hasn't seized this morning.", score: 0, note: "Phenobarbital is a maintenance drug, not given only when seizing — never skip a dose." },
        { t: "Give a double dose to be safe after the cluster.", score: 0, note: "Never double phenobarbital — overdose causes profound sedation and toxicity." },
      ],
    },
    rights: {
      prompt: "Before giving the 08:00 phenobarbital, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "Phenobarbital is a controlled (S4D/prescription) drug — record per policy", flag: true, coach: "Document the dose per your practice's medicines policy." },
        { t: "Confirm the exact dose against weight — precision matters for anti-epileptics", flag: true, coach: "Recheck mg/kg; small errors affect seizure control and toxicity." },
        { t: "Confirm Shadow's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Consistent dosing time vs the 20:00 dose", flag: true, coach: "Maintain the q12h interval to keep steady blood levels." },
        { t: "Drug, dose, route and time match the chart", flag: false, coach: "" },
        { t: "He seems sleepy so just skip it today", flag: true, coach: "Sedation may be a phenobarbital effect, but you don't skip a maintenance anti-epileptic — flag concerns to the vet." },
        { t: "The phenobarbital tablets are in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Phenobarbital",
      weightKg: 20,
      doseMgKg: 2.5,
      concMgPerMl: 10,
      route: "PO",
      prompt: "Phenobarbital is ordered at 2.5 mg/kg for Shadow (20 kg). Using the 10 mg/mL oral liquid, work out the total dose and the volume.",
    },
    routeTask: {
      prompt: "The chart specifies the oral route. What's the correct technique?",
      options: [
        { t: "Give the measured oral dose, confirm he swallows it, and ensure the exact time is captured for the q12h schedule.", score: 3, note: "Confirm swallowing and record the precise time to maintain stable levels." },
        { t: "Leave the tablet in his bowl and assume he'll eat it.", score: 0, note: "An uneaten anti-epileptic risks a missed dose and breakthrough seizures." },
        { t: "Crush and give IV for faster action.", score: 0, note: "Never give an oral preparation IV; route changes need veterinary direction." },
        { t: "Give it but don't note the exact time.", score: 1, note: "Exact timing matters for anti-epileptic blood levels — always record it." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 08:00 dose with the exact time, record any seizure activity/sedation, note it per the medicines policy, and check the CRI.", score: 3, note: "Precise timing, seizure/sedation notes and correct documentation support safe ongoing control." },
        { t: "Sign it later when you remember.", score: 0, note: "Records must be contemporaneous, especially for timed anti-epileptics." },
        { t: "Tell the next shift verbally that it's done.", score: 0, note: "A written, signed record is required." },
        { t: "Sign it but don't record his seizure activity.", score: 1, note: "Seizure frequency/sedation guides dose adjustment — record it." },
      ],
    },
  },

  {
    id: "rocky-cellulitis",
    name: "Rocky",
    species: "dog",
    breed: "Boxer",
    signalment: "5 yr · male neutered",
    weightLabel: "28 kg",
    ward: "General ward · Kennel 5",
    when: "Day 1 · 16:00 medication round",
    nowTime: "16:00",
    briefing:
      "Rocky is in for IV antibiotics for severe cellulitis of a hind limb. The 16:00 cefazolin is due. The cephalic catheter site looks slightly red and swollen and he flinches when it's touched.",
    sheet: {
      title: "Inpatient treatment sheet · Rocky (28 kg)",
      rows: [
        { time: "08:00", drug: "Cefazolin 22 mg/kg IV", freq: "q8h", status: "signed", note: "Given 08:05 — MT" },
        { time: "16:00", drug: "Cefazolin 22 mg/kg IV", freq: "q8h", status: "due", note: "Not yet given" },
        { time: "16:00", drug: "Hartmann's CRI", freq: "60 mL/h", status: "running", note: "Via the cephalic line" },
      ],
    },
    sheetTask: {
      prompt: "It's 16:00. The cefazolin is due IV, but the catheter site looks red and sore. What do you do?",
      options: [
        { t: "Don't use the suspect line — assess the catheter (red, swollen, painful suggests phlebitis/extravasation), flag it, and replace the catheter before giving the IV antibiotic.", score: 3, note: "Correct — a red, painful IV site signals phlebitis/extravasation; don't administer through it, replace the catheter first." },
        { t: "Push the cefazolin through the line quickly before it gets worse.", score: 0, note: "Giving drugs through a compromised line risks extravasation and tissue damage." },
        { t: "Give it IM instead without asking anyone.", score: 0, note: "Don't change the prescribed IV route without veterinary direction." },
        { t: "Skip the dose entirely.", score: 1, note: "The antibiotic is needed — don't skip it; resite the catheter and give it correctly." },
      ],
    },
    rights: {
      prompt: "Before giving the 16:00 cefazolin, run your checks. Tap every genuine concern to resolve first.",
      chips: [
        { t: "The IV catheter site is red, swollen and painful (possible phlebitis)", flag: true, coach: "A compromised line must be assessed and replaced before IV drugs are given." },
        { t: "Confirm catheter patency with a flush before any IV drug", flag: true, coach: "Always confirm patency; here the site signs mean it likely needs resiting." },
        { t: "Confirm Rocky's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Drug, dose, route and time match the chart", flag: false, coach: "" },
        { t: "Cefazolin should be given at the recommended IV rate", flag: false, coach: "" },
        { t: "It's only mild redness so it's fine to use the line", flag: true, coach: "Even early phlebitis warrants assessment and usually resiting before IV drugs." },
        { t: "The cefazolin is reconstituted correctly and in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Cefazolin",
      weightKg: 28,
      doseMgKg: 22,
      concMgPerMl: 100,
      route: "IV",
      prompt: "Cefazolin is ordered at 22 mg/kg IV for Rocky (28 kg). The reconstituted vial is 100 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "You've placed a fresh, patent catheter in the other leg. What's the safe technique?",
      options: [
        { t: "Confirm patency with a saline flush, give the cefazolin slowly IV at the recommended rate, then flush — and document the resited catheter.", score: 3, note: "Confirm patency on the new line, give at the correct rate, flush, and record the resite." },
        { t: "Give it as a fast bolus to catch up the schedule.", score: 0, note: "Give IV antibiotics at the recommended rate to avoid reactions." },
        { t: "Use the old, red catheter since the new one is in.", score: 0, note: "Never use the compromised line — that's why you resited it." },
        { t: "Give it IV but skip the flush.", score: 1, note: "Always flush before and after to confirm patency and clear the line." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 16:00 cefazolin with the time given, document the phlebitis and catheter replacement, note the new site, and flag the limb for monitoring.", score: 3, note: "Records the dose, the catheter problem and the resite — important for ongoing care and audit." },
        { t: "Just sign the antibiotic; the catheter change isn't worth noting.", score: 0, note: "Catheter complications and resites must be documented." },
        { t: "Sign it at the end of the day.", score: 0, note: "Records must be contemporaneous." },
        { t: "Note the dose but not which limb the new catheter is in.", score: 1, note: "Record the catheter site so the next shift knows which line is current." },
      ],
    },
  },

  {
    id: "ginger-postop-cat",
    name: "Ginger",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "3 yr · female spayed",
    weightLabel: "3.2 kg",
    ward: "Cat ward · Kennel 1",
    when: "Day 1 post-op · 10:00 medication round",
    nowTime: "10:00",
    briefing:
      "Ginger had a routine spay this morning. She's recovering well. The 10:00 meloxicam is written up. Feline NSAID dosing is very low and must be precise — an error in cats is dangerous.",
    sheet: {
      title: "Inpatient treatment sheet · Ginger (3.2 kg)",
      rows: [
        { time: "10:00", drug: "Meloxicam 0.05 mg/kg SC", freq: "once (post-op)", status: "due", note: "Single post-op dose" },
        { time: "10:00", drug: "Buprenorphine 0.02 mg/kg OTM", freq: "q8h", status: "signed", note: "Given 09:30 — RS" },
        { time: "10:00", drug: "Recovery monitoring", freq: "q15min", status: "running", note: "Temp/pulse/resp" },
      ],
    },
    sheetTask: {
      prompt: "It's 10:00. What's due for Ginger?",
      options: [
        { t: "The single post-op meloxicam SC — calculated precisely for her low body weight; the buprenorphine was already given at 09:30.", score: 3, note: "Correct — give the carefully-calculated feline NSAID dose; the opioid is done. Feline meloxicam dosing is tiny, so accuracy is vital." },
        { t: "Re-give the buprenorphine because she's only just out of surgery.", score: 0, note: "The opioid was given at 09:30 and is q8h — not due again yet." },
        { t: "Round the meloxicam dose up generously to be sure she's comfortable.", score: 0, note: "Never round a feline NSAID up — cats are very sensitive to NSAID overdose." },
        { t: "Skip the meloxicam — cats can't have NSAIDs at all.", score: 1, note: "Cats CAN have licensed feline NSAIDs at the correct low dose; precise dosing is the key." },
      ],
    },
    rights: {
      prompt: "Before giving the meloxicam SC, run your checks. Tap every genuine concern to resolve first.",
      chips: [
        { t: "Recalculate the tiny feline dose carefully — cats are NSAID-sensitive", flag: true, coach: "Feline NSAID doses are very small; a decimal error is dangerous — double-check the calculation." },
        { t: "Confirm she's well perfused/hydrated post-op before an NSAID", flag: true, coach: "NSAIDs are risky if hypovolaemic; confirm a stable, well-perfused recovery." },
        { t: "Use the LOW-concentration feline meloxicam preparation, not the dog strength", flag: true, coach: "Using the canine-strength product risks a large overdose — confirm the cat formulation." },
        { t: "Confirm Ginger's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Drug, dose, route (SC) and time match the chart", flag: false, coach: "" },
        { t: "It's a tiny dose so accuracy doesn't really matter", flag: true, coach: "The opposite — small patients are LESS forgiving of dosing errors." },
        { t: "The meloxicam is in date and not discoloured", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Meloxicam (feline)",
      weightKg: 3.2,
      doseMgKg: 0.05,
      concMgPerMl: 0.5,
      route: "SC",
      prompt: "Meloxicam is ordered at 0.05 mg/kg SC for Ginger (3.2 kg). The FELINE oral/injectable strength is 0.5 mg/mL. Work out the total dose and the volume — accuracy is critical in a cat.",
    },
    routeTask: {
      prompt: "The chart specifies meloxicam SC. What's the correct technique?",
      options: [
        { t: "Draw up the exact volume in a small (e.g. insulin/1 mL) syringe for accuracy, give SC into a loose skin fold with low-stress handling, and check the recovery is stable.", score: 3, note: "A small syringe ensures an accurate tiny dose; gentle SC technique suits a recovering cat." },
        { t: "Use a 10 mL syringe — it's what's to hand.", score: 0, note: "A large syringe can't measure a tiny feline volume accurately — use a small syringe." },
        { t: "Give it IV for faster effect.", score: 0, note: "Don't change the prescribed SC route without veterinary direction." },
        { t: "Estimate the volume by eye to save time.", score: 0, note: "Never estimate a feline NSAID dose — measure it precisely." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the meloxicam with the time given, record the exact dose/volume, note it as the single post-op NSAID, and continue recovery monitoring.", score: 3, note: "Documents the precise dose and the single-dose nature, with ongoing monitoring." },
        { t: "Sign it but don't record the exact volume.", score: 1, note: "Recording the exact dose/volume matters for a high-risk feline NSAID." },
        { t: "Sign it as a q24h ongoing order.", score: 0, note: "It was a single post-op dose — don't imply it repeats." },
        { t: "Leave it for the next shift to sign.", score: 0, note: "Sign your own administration contemporaneously." },
      ],
    },
  },

  {
    id: "molly-pancreatitis",
    name: "Molly",
    species: "dog",
    breed: "Cocker Spaniel",
    signalment: "8 yr · female spayed",
    weightLabel: "12 kg",
    ward: "Medical ward · Kennel 4",
    when: "Day 1 · 08:00 medication round",
    nowTime: "08:00",
    briefing:
      "Molly is hospitalised with pancreatitis on IV fluids, an anti-emetic and analgesia. The 08:00 maropitant is due. She has been nauseous and drooling.",
    sheet: {
      title: "Inpatient treatment sheet · Molly (12 kg)",
      rows: [
        { time: "08:00", drug: "Maropitant 1 mg/kg IV", freq: "q24h", status: "due", note: "Anti-emetic — not yet given" },
        { time: "08:00", drug: "Buprenorphine 0.02 mg/kg IV", freq: "q8h", status: "signed", note: "Given 07:00 — DL" },
        { time: "08:00", drug: "Hartmann's CRI", freq: "40 mL/h", status: "running", note: "Bag 3" },
      ],
    },
    sheetTask: {
      prompt: "It's 08:00. What's due for Molly?",
      options: [
        { t: "The 08:00 maropitant IV — it's due and unsigned; the buprenorphine was given at 07:00 and the CRI is running.", score: 3, note: "Correct — give the anti-emetic that's due; the opioid is done and the fluids are running." },
        { t: "Re-give the buprenorphine because she's still uncomfortable.", score: 0, note: "It was given at 07:00 and is q8h — flag breakthrough pain to the vet rather than re-dosing early." },
        { t: "Skip the maropitant because she's already drooling.", score: 0, note: "Maropitant is given precisely because she's nauseous — don't withhold it." },
        { t: "Give the maropitant SC instead to avoid the line.", score: 1, note: "The chart says IV; don't change route without veterinary direction." },
      ],
    },
    rights: {
      prompt: "Before giving the 08:00 maropitant, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "The 08:00 dose is unsigned — confirm it wasn't already given", flag: true, coach: "An unsigned due dose must be clarified to avoid a double dose." },
        { t: "Confirm Molly's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Check IV catheter patency before giving the drug IV", flag: true, coach: "Confirm the line is patent and clean before any IV administration." },
        { t: "Maropitant 1 mg/kg IV is the standard dose/route", flag: false, coach: "" },
        { t: "Record her nausea/pain so analgesia and anti-emesis can be adjusted", flag: true, coach: "Document clinical signs to guide ongoing management." },
        { t: "She's been here a while so skip the checks", flag: true, coach: "Familiarity never removes the medication checks." },
        { t: "The maropitant vial is in date and clear", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Maropitant",
      weightKg: 12,
      doseMgKg: 1,
      concMgPerMl: 10,
      route: "IV",
      prompt: "Maropitant is ordered at 1 mg/kg IV for Molly (12 kg). The vial is 10 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies maropitant IV. What's the correct technique?",
      options: [
        { t: "Confirm catheter patency with a flush, give the maropitant slowly IV (it can sting), then flush, monitoring her comfort.", score: 3, note: "Confirm patency, give slowly to reduce stinging, flush and monitor." },
        { t: "Inject it rapidly so you can move on.", score: 0, note: "Rapid IV maropitant stings more — give it slowly." },
        { t: "Give it IM because the line is slow.", score: 0, note: "Don't change the prescribed IV route without direction." },
        { t: "Give it IV but skip the flush.", score: 1, note: "Always flush before and after to confirm patency and clear the line." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 08:00 maropitant with the time given, record her nausea/comfort, check and document the CRI rate, and flag any breakthrough pain to the vet.", score: 3, note: "Contemporaneous sign-off with clinical notes and a fluid check, escalating pain appropriately." },
        { t: "Sign it later when convenient.", score: 0, note: "Records must be contemporaneous." },
        { t: "Sign for the buprenorphine too so the sheet looks complete.", score: 0, note: "Never sign for a drug you didn't give." },
        { t: "Initial the maropitant but don't note her ongoing nausea.", score: 1, note: "Document her clinical signs to guide management." },
      ],
    },
  },

  {
    id: "luna-blocked-cat",
    name: "Luna",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "5 yr · male neutered",
    weightLabel: "5 kg",
    ward: "Cat ward · Kennel 4",
    when: "Day 1 · 14:00 medication round",
    nowTime: "14:00",
    briefing:
      "Luna is a recently-unblocked male cat on IV fluids with a urinary catheter, being monitored for post-obstructive complications. The 14:00 buprenorphine is due. Electrolytes (especially potassium) are being watched.",
    sheet: {
      title: "Inpatient treatment sheet · Luna (5 kg)",
      rows: [
        { time: "14:00", drug: "Buprenorphine 0.02 mg/kg IV", freq: "q8h", status: "due", note: "Analgesia — not yet given" },
        { time: "14:00", drug: "Hartmann's CRI", freq: "20 mL/h", status: "running", note: "Post-obstructive diuresis — rate per output" },
        { time: "14:00", drug: "Urinary catheter / closed collection", freq: "monitor", status: "running", note: "Measure 'ins and outs'" },
      ],
    },
    sheetTask: {
      prompt: "It's 14:00. What's due, and what must you monitor in a post-obstructive cat?",
      options: [
        { t: "Give the 14:00 buprenorphine IV that's due, and measure urine output ('ins and outs') to match the CRI to the post-obstructive diuresis, watching for electrolyte changes.", score: 3, note: "Correct — give the due analgesia and monitor fluid balance/electrolytes closely; post-obstructive diuresis can be marked." },
        { t: "Just give the buprenorphine; fluid balance can be checked tomorrow.", score: 0, note: "Post-obstructive cats need close 'ins and outs' and electrolyte monitoring — not deferred." },
        { t: "Turn off the fluids now that he's unblocked.", score: 0, note: "Never stop the prescribed CRI; post-obstructive diuresis often needs MORE fluids, matched to output." },
        { t: "Give a double opioid dose because urinary pain is severe.", score: 0, note: "Never double the dose; flag inadequate analgesia to the vet." },
      ],
    },
    rights: {
      prompt: "Before giving the 14:00 buprenorphine IV, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "Buprenorphine is a controlled (S8) drug — register entry & witness", flag: true, coach: "Controlled drugs need a register entry and (per policy) a witness." },
        { t: "Confirm IV catheter patency before giving the drug IV", flag: true, coach: "Confirm the line is patent and clean before IV administration." },
        { t: "Monitor potassium/electrolytes during post-obstructive diuresis", flag: true, coach: "Electrolytes can swing markedly after relief of obstruction — keep monitoring." },
        { t: "Confirm Luna's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Drug, dose, route (IV) and time match the chart", flag: false, coach: "" },
        { t: "He's stable now so the urinary monitoring can stop", flag: true, coach: "Post-obstructive monitoring continues until he's confirmed stable — don't stop early." },
        { t: "The buprenorphine ampoule is in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Buprenorphine",
      weightKg: 5,
      doseMgKg: 0.02,
      concMgPerMl: 0.3,
      route: "IV",
      prompt: "Buprenorphine is ordered at 0.02 mg/kg IV for Luna (5 kg). The ampoule is 0.3 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies buprenorphine IV. What's the correct technique?",
      options: [
        { t: "Confirm catheter patency with a flush, give the buprenorphine slowly IV, then flush — using low-stress handling for a sore cat.", score: 3, note: "Confirm patency, give slowly, flush, and handle gently." },
        { t: "Give it as a fast bolus and move on.", score: 0, note: "Give slowly IV and monitor; rushing risks reactions and stress." },
        { t: "Give it via the urinary catheter line by mistake.", score: 0, note: "Drugs go via the IV line — never the urinary catheter; check what you're connecting to." },
        { t: "Give IV but skip the flush.", score: 1, note: "Always flush before and after to confirm patency and clear the line." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 14:00 buprenorphine with the time given, complete the controlled-drug register with a witness, record urine output and the CRI rate, and note electrolyte monitoring.", score: 3, note: "Sign-off, S8 register, 'ins and outs', fluid check and electrolyte monitoring — complete for a post-obstructive cat." },
        { t: "Sign the opioid and leave the urine output for later.", score: 1, note: "Urine output is central to managing this cat — record it now." },
        { t: "Initial the dose but skip the controlled-drug register.", score: 0, note: "The S8 register must be completed at administration." },
        { t: "Just give it and tidy up; the vet will check later.", score: 0, note: "Contemporaneous records and monitoring are your responsibility." },
      ],
    },
  },

  {
    id: "buster-gdv-postop",
    name: "Buster",
    species: "dog",
    breed: "Great Dane",
    signalment: "7 yr · male neutered",
    weightLabel: "55 kg",
    ward: "ICU · Kennel 1",
    when: "Day 1 post-op · 22:00 medication round",
    nowTime: "22:00",
    briefing:
      "Buster had emergency surgery for a gastric dilatation-volvulus (GDV) and is in ICU on a methadone CRI, IV fluids and ECG monitoring (arrhythmias are common after GDV). The 22:00 antibiotic is due.",
    sheet: {
      title: "Inpatient treatment sheet · Buster (55 kg) · ICU",
      rows: [
        { time: "22:00", drug: "Cefazolin 22 mg/kg IV", freq: "q8h", status: "due", note: "Not yet given" },
        { time: "22:00", drug: "Methadone CRI", freq: "0.1 mg/kg/h", status: "running", note: "Controlled-drug CRI — witnessed" },
        { time: "22:00", drug: "Hartmann's CRI", freq: "120 mL/h", status: "running", note: "Per cardiovascular status" },
        { time: "continuous", drug: "ECG monitoring", freq: "continuous", status: "running", note: "Watch for VPCs/arrhythmias" },
      ],
    },
    sheetTask: {
      prompt: "It's 22:00 in ICU. What's due, and what must you stay alert to after a GDV?",
      options: [
        { t: "Give the 22:00 cefazolin IV that's due, keep the methadone and fluid CRIs running as prescribed, and watch the ECG closely for post-GDV arrhythmias (VPCs).", score: 3, note: "Correct — give the due antibiotic, maintain the CRIs, and monitor for the cardiac arrhythmias common after GDV." },
        { t: "Stop the methadone CRI to 'wake him up' for the antibiotic.", score: 0, note: "Never stop a prescribed analgesic CRI; give the antibiotic without interrupting analgesia." },
        { t: "Ignore the ECG — arrhythmias don't happen after GDV.", score: 0, note: "Arrhythmias (VPCs/VT) are a well-known, dangerous post-GDV complication — monitor closely." },
        { t: "Give the cefazolin only if he wakes and asks for it.", score: 1, note: "Antibiotics are given on schedule regardless — and a post-op patient won't 'ask'." },
      ],
    },
    rights: {
      prompt: "Before giving the 22:00 cefazolin IV, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "ECG shows occasional VPCs — flag and monitor the arrhythmia", flag: true, coach: "Post-GDV ventricular arrhythmias must be flagged and monitored; the vet may intervene." },
        { t: "Methadone CRI is a controlled drug — running balance & witness records", flag: true, coach: "Controlled-drug CRIs need accurate, witnessed running records." },
        { t: "Confirm IV catheter patency and use a clean port for the antibiotic", flag: true, coach: "Confirm patency and avoid contaminating the CRI lines." },
        { t: "Confirm Buster's identity against the ICU kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Cefazolin dose, route and time match the chart", flag: false, coach: "" },
        { t: "He's sedated so the medication checks can be skipped", flag: true, coach: "A sedated ICU patient needs MORE careful checks, not fewer." },
        { t: "The cefazolin is reconstituted correctly and in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Cefazolin",
      weightKg: 55,
      doseMgKg: 22,
      concMgPerMl: 100,
      route: "IV",
      prompt: "Cefazolin is ordered at 22 mg/kg IV for Buster (55 kg). The reconstituted vial is 100 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies cefazolin IV. What's the correct technique in this ICU patient?",
      options: [
        { t: "Confirm patency, give the cefazolin slowly IV via a clean port (not interrupting the CRIs), flush, and keep watching the ECG.", score: 3, note: "Give via a clean port without disturbing the analgesic/fluid CRIs, at the correct rate, monitoring the ECG." },
        { t: "Inject it as a fast bolus into the CRI line.", score: 0, note: "Give at the recommended rate via a clean port; don't disrupt the CRIs or risk incompatibility." },
        { t: "Pause the methadone CRI to use that line.", score: 0, note: "Don't interrupt the analgesic CRI; use a separate clean port." },
        { t: "Give it IV but skip the flush.", score: 1, note: "Always flush to confirm patency and clear the line." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 22:00 cefazolin with the time given, document the ECG findings (VPCs) and that you've flagged them, update the methadone CRI running record with a witness, and note the fluid rate.", score: 3, note: "Complete ICU documentation: antibiotic, arrhythmia monitoring/escalation, controlled-CRI record and fluids." },
        { t: "Sign the antibiotic; the ECG isn't your job to note.", score: 0, note: "Monitoring and recording ECG changes is core ICU nursing." },
        { t: "Sign it at the end of the shift.", score: 0, note: "Records must be contemporaneous in ICU." },
        { t: "Record the antibiotic but not the methadone CRI balance.", score: 1, note: "Controlled-drug CRI running balances must be kept and witnessed." },
      ],
    },
  },

  {
    id: "pepper-rabbit",
    name: "Pepper",
    species: "rabbit",
    breed: "Netherland Dwarf",
    signalment: "3 yr · female entire",
    weightLabel: "1.4 kg",
    ward: "Exotics ward · Quiet kennel",
    when: "Day 1 · 09:00 medication round",
    nowTime: "09:00",
    briefing:
      "Pepper is a rabbit hospitalised with gut stasis (ileus). She's on a prokinetic, analgesia and assisted feeding. The 09:00 meloxicam is due. Rabbits are prey species — stress and the right gut support are critical.",
    sheet: {
      title: "Inpatient treatment sheet · Pepper (1.4 kg) · EXOTICS",
      rows: [
        { time: "09:00", drug: "Meloxicam 0.6 mg/kg PO", freq: "q24h", status: "due", note: "Rabbit NSAID dose — not yet given" },
        { time: "09:00", drug: "Metoclopramide 0.5 mg/kg SC", freq: "q8h", status: "signed", note: "Prokinetic — given 08:30 — EX" },
        { time: "09:00", drug: "Assisted (syringe) feeding", freq: "q4–6h", status: "due", note: "Critical-care herbivore formula" },
        { time: "09:00", drug: "Hartmann's SC/IV", freq: "per plan", status: "running", note: "Fluid support" },
      ],
    },
    sheetTask: {
      prompt: "It's 09:00. What's due for this rabbit with gut stasis?",
      options: [
        { t: "Give the 09:00 meloxicam at the (higher) rabbit dose and provide assisted feeding — keeping the gut moving is central to treating ileus; the prokinetic was given at 08:30.", score: 3, note: "Correct — rabbits need their NSAID at the right (higher mg/kg) dose AND ongoing fibre/assisted feeding to resolve stasis." },
        { t: "Withhold food to 'rest' the gut.", score: 0, note: "The opposite — a rabbit's gut must keep moving; assisted feeding is essential in ileus." },
        { t: "Give the meloxicam at the dog/cat dose to be cautious.", score: 0, note: "Rabbits need a higher mg/kg NSAID dose than dogs/cats — using the small-animal dose under-treats pain." },
        { t: "Re-give the metoclopramide now as well.", score: 1, note: "It was given at 08:30 and is q8h — not due again; give the meloxicam and feed." },
      ],
    },
    rights: {
      prompt: "Before giving the 09:00 meloxicam, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "Use the correct (higher) rabbit NSAID dose, not the dog/cat dose", flag: true, coach: "Rabbit meloxicam dosing is higher per kg than dogs/cats — confirm the exotic dose." },
        { t: "Minimise stress — rabbits are prey animals and stress worsens ileus", flag: true, coach: "Low-stress, quiet handling is part of safe treatment in rabbits." },
        { t: "Confirm she's eating/has gut sounds and is producing faecal pellets", flag: true, coach: "Gut motility and pellet output are key monitoring points in stasis." },
        { t: "Confirm Pepper's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Drug, dose, route (PO) and time match the chart", flag: false, coach: "" },
        { t: "Rabbits are just small cats so use feline doses", flag: true, coach: "Rabbits are NOT small cats — their physiology and dosing differ markedly." },
        { t: "The meloxicam is in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Meloxicam (rabbit)",
      weightKg: 1.4,
      doseMgKg: 0.6,
      concMgPerMl: 1.5,
      route: "PO",
      prompt: "Meloxicam is ordered at 0.6 mg/kg PO for Pepper (1.4 kg) — note the higher rabbit dose. The suspension is 1.5 mg/mL. Work out the total dose and the volume.",
    },
    routeTask: {
      prompt: "The chart specifies oral meloxicam, plus assisted feeding. What's the correct technique?",
      options: [
        { t: "Give the measured oral dose gently, then syringe-feed the critical-care herbivore formula slowly with low-stress handling, watching she swallows and doesn't aspirate.", score: 3, note: "Accurate oral dosing plus careful syringe feeding supports gut motility while minimising stress and aspiration risk." },
        { t: "Force the syringe quickly to get it over with.", score: 0, note: "Rushing risks aspiration and severe stress in a prey species — go slowly." },
        { t: "Give the meloxicam IV instead.", score: 0, note: "Don't change the prescribed oral route without veterinary direction." },
        { t: "Skip the feeding — the prokinetic alone will fix the stasis.", score: 0, note: "Assisted feeding (fibre) is essential to resolve rabbit ileus." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the meloxicam with the time given, record the amount of assisted feed taken, note gut sounds and faecal pellet output, and flag any deterioration.", score: 3, note: "Documents the dose, feeding, and the key motility/output monitoring for a stasis rabbit." },
        { t: "Sign the meloxicam; feeding amounts aren't worth recording.", score: 0, note: "Feed intake and pellet output are central monitoring points in ileus — record them." },
        { t: "Sign it at the end of the shift.", score: 0, note: "Records must be contemporaneous." },
        { t: "Record the drug but not the gut sounds/pellets.", score: 1, note: "Gut motility signs guide management in a stasis rabbit — record them." },
      ],
    },
  },

  {
    id: "tilly-csection",
    name: "Tilly",
    species: "dog",
    breed: "French Bulldog",
    signalment: "3 yr · female entire",
    weightLabel: "11 kg",
    ward: "Maternity / recovery · Kennel 2",
    when: "Day 1 post-op · 12:00 medication round",
    nowTime: "12:00",
    briefing:
      "Tilly had an emergency caesarean this morning and is recovering with four nursing pups. The 12:00 analgesia is due. Drug choice matters because she is lactating — some drugs pass into milk and affect the pups.",
    sheet: {
      title: "Inpatient treatment sheet · Tilly (11 kg) · lactating",
      rows: [
        { time: "12:00", drug: "Methadone 0.2 mg/kg IV", freq: "q6h", status: "due", note: "Analgesia — not yet given" },
        { time: "12:00", drug: "Amoxicillin-clav 12.5 mg/kg IV", freq: "q12h", status: "signed", note: "Given 11:00 — VN" },
        { time: "12:00", drug: "Hartmann's CRI", freq: "30 mL/h", status: "running", note: "Maintenance" },
        { time: "ongoing", drug: "Pup welfare / nursing", freq: "monitor", status: "running", note: "Warmth, latching, weights" },
      ],
    },
    sheetTask: {
      prompt: "It's 12:00. What's due, and what's special about medicating a lactating bitch?",
      options: [
        { t: "Give the 12:00 methadone IV that's due for post-op pain, and be aware drugs can pass into milk — monitor the pups for sedation and support nursing/warmth.", score: 3, note: "Correct — provide effective analgesia (uncontrolled pain harms mothering) while monitoring the pups, since drugs can transfer in milk." },
        { t: "Withhold all pain relief so nothing reaches the pups.", score: 0, note: "Leaving a post-caesarean bitch in pain is unacceptable and harms mothering — use appropriate analgesia and monitor the pups." },
        { t: "Re-give the antibiotic now too.", score: 0, note: "It was given at 11:00 and is q12h — not due; give the analgesia." },
        { t: "Give a double methadone dose so it lasts longer.", score: 0, note: "Never double an opioid dose; follow the q6h schedule." },
      ],
    },
    rights: {
      prompt: "Before giving the 12:00 methadone IV, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "Methadone is a controlled (S8) drug — register entry & witness", flag: true, coach: "Controlled drugs need a register entry and (per policy) a witness." },
        { t: "She is lactating — monitor the pups for sedation as drugs pass into milk", flag: true, coach: "Note the lactation; watch the pups and support nursing." },
        { t: "Confirm IV catheter patency before the IV drug", flag: true, coach: "Confirm the line is patent and clean before IV administration." },
        { t: "Confirm Tilly's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Methadone dose, route (IV) and time match the chart", flag: false, coach: "" },
        { t: "Pain relief should be skipped entirely in nursing mothers", flag: true, coach: "Untreated pain is harmful and reduces mothering — appropriate analgesia is given WITH pup monitoring." },
        { t: "The methadone ampoule is in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Methadone",
      weightKg: 11,
      doseMgKg: 0.2,
      concMgPerMl: 10,
      route: "IV",
      prompt: "Methadone is ordered at 0.2 mg/kg IV for Tilly (11 kg). The vial is 10 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies methadone IV. What's the correct technique?",
      options: [
        { t: "Confirm catheter patency with a flush, give the methadone slowly IV, then flush — and continue to monitor both Tilly and the pups (warmth, latching, sedation).", score: 3, note: "Confirm patency, give slowly, flush, and monitor the dam and her litter." },
        { t: "Give it as a fast bolus and return the pups quickly.", score: 0, note: "Give slow IV opioids and monitor; rushing risks reactions and disrupts the litter." },
        { t: "Give it IM into a pup by mistake while they're nearby.", score: 0, note: "Always confirm you're medicating the right patient — keep pups safely separated during administration." },
        { t: "Give IV but skip the flush.", score: 1, note: "Always flush before and after to confirm patency and clear the line." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 12:00 methadone with the time given, complete the controlled-drug register with a witness, record Tilly's pain score and the pups' welfare (weights/latching/sedation), and check the CRI.", score: 3, note: "Complete record: sign-off, S8 register, maternal pain score and litter monitoring." },
        { t: "Sign the methadone; the pups aren't part of the record.", score: 1, note: "Litter welfare is important here and should be documented." },
        { t: "Initial the dose but skip the controlled-drug register.", score: 0, note: "The S8 register must be completed at administration." },
        { t: "Sign it at the end of the shift.", score: 0, note: "Records must be contemporaneous." },
      ],
    },
  },

  {
    id: "max-diabetic-dog",
    name: "Max",
    species: "dog",
    breed: "Dachshund",
    signalment: "9 yr · male neutered",
    weightLabel: "9 kg",
    ward: "Medical ward · Kennel 3",
    when: "Day 1 · 08:00 medication round",
    nowTime: "08:00",
    briefing:
      "Max is a hospitalised diabetic stabilising on insulin. The 08:00 insulin is written up to be given WITH his morning feed. He has eaten only half his breakfast and a blood glucose is pending.",
    sheet: {
      title: "Inpatient treatment sheet · Max (9 kg) · DIABETIC",
      rows: [
        { time: "08:00", drug: "Caninsulin (insulin) 4 IU SC", freq: "q12h", status: "due", note: "Give WITH food; check BG first" },
        { time: "08:00", drug: "Morning feed (measured diabetic diet)", freq: "q12h", status: "query", note: "Only half eaten" },
        { time: "08:00", drug: "Blood glucose curve sampling", freq: "per plan", status: "due", note: "BG pending" },
      ],
    },
    sheetTask: {
      prompt: "It's 08:00. Max has eaten only half his breakfast and the blood glucose isn't back. What do you do about the insulin?",
      options: [
        { t: "Don't give the full insulin dose yet — check the blood glucose and how much he's eaten, and consult the vet, as a full insulin dose with reduced intake risks hypoglycaemia.", score: 3, note: "Correct — insulin is tied to food intake and glucose; giving a full dose to a dog that ate little risks dangerous hypoglycaemia. Check BG and ask the vet." },
        { t: "Give the full 4 IU regardless — the schedule says 08:00.", score: 0, note: "Giving full insulin when he's eaten little (and BG unknown) risks hypoglycaemia — the dose must be tied to intake/glucose." },
        { t: "Give double insulin because his glucose is probably high.", score: 0, note: "Never guess insulin doses or double them — this is extremely dangerous." },
        { t: "Skip the glucose check; insulin doesn't depend on it.", score: 0, note: "Glucose monitoring is essential to safe insulin dosing." },
      ],
    },
    rights: {
      prompt: "Before any insulin is given, run your checks. Tap every genuine concern to resolve first.",
      chips: [
        { t: "He's only eaten half his food — risk of hypoglycaemia with a full dose", flag: true, coach: "Insulin dosing depends on food intake; reduced intake means the dose must be reviewed with the vet." },
        { t: "Blood glucose result is pending — needed to dose safely", flag: true, coach: "Check the BG before giving insulin to avoid hypoglycaemia." },
        { t: "Confirm it's insulin syringe/units (not mL) and the correct insulin type", flag: true, coach: "Insulin is dosed in IU using an insulin-specific syringe — unit/syringe errors are dangerous." },
        { t: "Confirm Max's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "The insulin was gently rolled (not shaken) and is in date", flag: false, coach: "" },
        { t: "Insulin can always be given on time regardless of food", flag: true, coach: "No — insulin is tied to feeding and glucose; never give blindly on the clock." },
        { t: "The insulin vial is the prescribed product/strength", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Glucose check (mmol/L)",
      weightKg: 9,
      doseMgKg: 0,
      concMgPerMl: 1,
      route: "SC",
      prompt: "Insulin units are NOT weight-calculated here — they are prescribed and tied to food/glucose. As a numeric check, record Max's latest blood glucose reading in mmol/L that the vet quotes you: enter 5 (a normal in-range value) to confirm you've checked it before any dosing decision.",
    },
    routeTask: {
      prompt: "The vet reviews the BG and intake and OKs a reduced insulin dose once Max finishes a little more food. What's the correct technique?",
      options: [
        { t: "Encourage him to eat a bit more, then draw the prescribed reduced dose into an INSULIN syringe and give SC into a skin fold, confirming the units with a colleague.", score: 3, note: "Tie insulin to food, use an insulin syringe, double-check the units, and give SC." },
        { t: "Use a normal 1 mL syringe and estimate the units.", score: 0, note: "Always use an insulin syringe calibrated in IU — never estimate insulin." },
        { t: "Give it IV for a faster effect.", score: 0, note: "This insulin is given SC; don't change the route without direction." },
        { t: "Give the original full dose now that he's had a few more bites.", score: 1, note: "Follow the vet's reduced-dose instruction, not the original dose." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Record the blood glucose, how much he ate, the vet review, the reduced insulin dose/time given (in IU) and who checked it, and continue glucose monitoring.", score: 3, note: "Documents BG, intake, the dose decision, the double-check and ongoing monitoring — exactly right for a hospitalised diabetic." },
        { t: "Just write 'insulin given 08:00'.", score: 0, note: "For insulin you must record the units, the BG and food intake — not just a tick." },
        { t: "Sign for the full original dose to match the chart.", score: 0, note: "Never record a dose you didn't give; document the actual reduced dose." },
        { t: "Record the insulin but not the glucose or food intake.", score: 1, note: "BG and intake are essential context for safe diabetic records." },
      ],
    },
  },

  {
    id: "bonnie-chemo",
    name: "Bonnie",
    species: "dog",
    breed: "Golden Retriever",
    signalment: "8 yr · female spayed",
    weightLabel: "30 kg",
    ward: "Day ward · Kennel 4",
    when: "Day visit · 11:00 medication round",
    nowTime: "11:00",
    briefing:
      "Bonnie is here for a scheduled chemotherapy (cytotoxic) treatment for lymphoma, plus a pre-treatment anti-emetic. Cytotoxic drugs need special safe-handling. The 11:00 maropitant is due before the cytotoxic.",
    sheet: {
      title: "Inpatient treatment sheet · Bonnie (30 kg) · CYTOTOXIC handling",
      rows: [
        { time: "11:00", drug: "Maropitant 1 mg/kg IV", freq: "pre-chemo", status: "due", note: "Anti-emetic before cytotoxic" },
        { time: "11:15", drug: "Vincristine (cytotoxic) IV", freq: "per protocol", status: "later", note: "VET to administer — PPE, spill kit" },
        { time: "11:00", drug: "IV catheter (clean, first-stick)", freq: "check", status: "running", note: "Patency vital for a vesicant" },
      ],
    },
    sheetTask: {
      prompt: "It's 11:00. What's your role around this chemotherapy appointment?",
      options: [
        { t: "Give the 11:00 maropitant IV as charted, ensure a clean, well-placed and patent catheter (vital because the cytotoxic is a vesicant), and prepare PPE and the spill kit for the vet to administer the cytotoxic.", score: 3, note: "Correct — give the anti-emetic, guarantee catheter patency for a vesicant, and set up safe cytotoxic handling (PPE, closed systems, spill kit)." },
        { t: "Administer the vincristine yourself to save the vet time.", score: 0, note: "Cytotoxic administration follows strict protocols/role boundaries — don't give it outside your authorisation/training." },
        { t: "Use any old catheter; patency doesn't matter for chemo.", score: 0, note: "Vesicant extravasation causes severe tissue damage — a patent, well-placed catheter is essential." },
        { t: "Skip the PPE — it's only one injection.", score: 0, note: "Cytotoxic safe-handling (PPE, closed systems) protects you and the team every time." },
      ],
    },
    rights: {
      prompt: "Before the 11:00 maropitant and chemo prep, run your checks. Tap every genuine concern to resolve.",
      chips: [
        { t: "Cytotoxic safe-handling: PPE, closed-system devices and a spill kit ready", flag: true, coach: "Cytotoxics are hazardous — full safe-handling precautions every time." },
        { t: "Catheter must be clean, first-stick and confirmed patent (vesicant risk)", flag: true, coach: "Vincristine is a vesicant — extravasation causes severe tissue necrosis; patency is critical." },
        { t: "Confirm today's protocol/dose has been checked and authorised by the vet", flag: true, coach: "Chemo doses are precisely protocol-driven and vet-authorised — verify before proceeding." },
        { t: "Confirm Bonnie's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Maropitant dose, route (IV) and time match the chart", flag: false, coach: "" },
        { t: "Cytotoxics can be handled like any other injection", flag: true, coach: "No — they require special handling, PPE and waste disposal." },
        { t: "The maropitant vial is in date and clear", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Maropitant",
      weightKg: 30,
      doseMgKg: 1,
      concMgPerMl: 10,
      route: "IV",
      prompt: "The pre-chemo maropitant is ordered at 1 mg/kg IV for Bonnie (30 kg). The vial is 10 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "For the maropitant (your role) the chart specifies IV. What's the correct technique?",
      options: [
        { t: "Confirm catheter patency with a saline flush, give the maropitant slowly IV, flush, and confirm the line remains clean and patent for the vet's cytotoxic.", score: 3, note: "Confirm patency, give slowly, flush, and protect the line for the vesicant that follows." },
        { t: "Give it fast and don't bother flushing — the vet will sort the line.", score: 0, note: "A reliable, patent line is essential before a vesicant — flush and confirm." },
        { t: "Use the same syringe later for the cytotoxic to save plastic.", score: 0, note: "Never reuse syringes, and especially not with cytotoxics — strict single-use/closed-system handling applies." },
        { t: "Give the maropitant IM instead.", score: 1, note: "The chart says IV and a patent line is needed anyway — follow the prescribed route." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 11:00 maropitant with the time given, document the catheter patency and that cytotoxic safe-handling/PPE was used, and note the vet administered the vincristine per protocol.", score: 3, note: "Records the anti-emetic, catheter status, safe-handling and the supervised cytotoxic administration." },
        { t: "Sign the maropitant; the chemo handling isn't worth noting.", score: 0, note: "Cytotoxic handling and administration must be clearly documented." },
        { t: "Record that YOU gave the vincristine.", score: 0, note: "Don't record administering a drug you weren't authorised to give — document who actually gave it." },
        { t: "Sign the maropitant but not the catheter patency.", score: 1, note: "Catheter patency is critical with a vesicant and should be recorded." },
      ],
    },
  },

  {
    id: "oscar-abscess-cat",
    name: "Oscar",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "4 yr · male neutered",
    weightLabel: "5.5 kg",
    ward: "Cat ward · Kennel 5",
    when: "Day 1 · 10:00 medication round",
    nowTime: "10:00",
    briefing:
      "Oscar is an outdoor cat in with a cat-bite abscess that was lanced and flushed. He's on antibiotics and analgesia. The 10:00 amoxicillin-clav (SC) is due.",
    sheet: {
      title: "Inpatient treatment sheet · Oscar (5.5 kg)",
      rows: [
        { time: "10:00", drug: "Amoxicillin-clav 12.5 mg/kg SC", freq: "q12h", status: "due", note: "Not yet given" },
        { time: "10:00", drug: "Meloxicam 0.05 mg/kg PO", freq: "q24h", status: "signed", note: "Given with food 08:30 — KP" },
        { time: "10:00", drug: "Warm compress / wound care", freq: "q8h", status: "due", note: "Encourage drainage" },
      ],
    },
    sheetTask: {
      prompt: "It's 10:00. What's due for Oscar?",
      options: [
        { t: "Give the 10:00 amoxicillin-clav SC that's due, and carry out the wound care/warm compress; the meloxicam was already given at 08:30.", score: 3, note: "Correct — give the due antibiotic and do the wound care; the NSAID is done for the day." },
        { t: "Re-give the meloxicam because the wound looks sore.", score: 0, note: "The NSAID is once daily and already given — repeating it risks toxicity; manage pain via the vet if inadequate." },
        { t: "Skip the antibiotic since the abscess was already lanced.", score: 0, note: "Antibiotics are continued as charted after drainage — don't skip the due dose." },
        { t: "Give the antibiotic IV instead to work faster.", score: 1, note: "The chart says SC; don't change the route without veterinary direction." },
      ],
    },
    rights: {
      prompt: "Before giving the 10:00 amoxicillin-clav SC, run your checks. Tap every genuine concern to resolve first.",
      chips: [
        { t: "Confirm Oscar's identity against the kennel card", flag: true, coach: "Right patient before any drug." },
        { t: "Inject SC away from the wound/abscess site into healthy skin", flag: true, coach: "Don't inject into or near infected/inflamed tissue — choose a healthy SC site." },
        { t: "Drug, dose, route (SC) and time match the chart", flag: false, coach: "" },
        { t: "Record his demeanour and the wound's appearance/drainage", flag: true, coach: "Document wound progress to guide ongoing care." },
        { t: "Amoxicillin-clav 12.5 mg/kg SC is the standard dose/route", flag: false, coach: "" },
        { t: "He's a fractious outdoor cat so skip the checks and be quick", flag: true, coach: "A fractious cat needs careful low-stress handling and full checks — not a rushed, unsafe approach." },
        { t: "The antibiotic is reconstituted correctly and in date", flag: false, coach: "" },
      ],
    },
    calc: {
      drug: "Amoxicillin-clav",
      weightKg: 5.5,
      doseMgKg: 12.5,
      concMgPerMl: 100,
      route: "SC",
      prompt: "Amoxicillin-clav is ordered at 12.5 mg/kg SC for Oscar (5.5 kg). The reconstituted concentration is 100 mg/mL. Work out the total dose and the volume to draw up.",
    },
    routeTask: {
      prompt: "The chart specifies the SC route. What's the correct technique for this cat?",
      options: [
        { t: "Give SC into a loose scruff/flank fold of HEALTHY skin away from the wound, using low-stress handling, and check the skin is intact.", score: 3, note: "Correct SC technique into healthy skin away from the abscess, with calm handling." },
        { t: "Inject it directly into the abscess cavity.", score: 0, note: "Never inject into infected/abscess tissue — use a healthy SC site." },
        { t: "Give it IV down a catheter you place specially.", score: 0, note: "Don't change the prescribed SC route without veterinary direction." },
        { t: "Scruff him hard and inject fast regardless of stress.", score: 1, note: "Use low-stress handling; forceful restraint raises stress and bite risk." },
      ],
    },
    record: {
      prompt: "How do you complete the round?",
      options: [
        { t: "Initial the 10:00 antibiotic with the time given, record the wound appearance/drainage and the wound care done, and note his demeanour for the next shift.", score: 3, note: "Contemporaneous sign-off plus wound-care documentation guides ongoing management." },
        { t: "Sign the antibiotic; the wound care isn't worth noting.", score: 0, note: "Wound progress and care must be documented for an abscess case." },
        { t: "Sign it at the end of the shift.", score: 0, note: "Records must be contemporaneous." },
        { t: "Record the dose but not the wound's appearance.", score: 1, note: "Wound appearance/drainage is key monitoring — record it." },
      ],
    },
  },
];
